// App 怎麼決定雲端環境（SPEC #platform-791，Eddy 2026-10-05）—— 純函式，不碰 DOM / storage（vite.config.ts 也 import）。
//
// identity 位址由品牌 .env 注入（brand.yaml identity.base_urls），沒設就建置失敗（cloudEnvConfigError），程式裡不寫任何預設位址：
//   VITE_IDENTITY_BASE_PROD / VITE_IDENTITY_BASE_DEV（必填，https://）、VITE_API_BASE（未登入的掃貼紙救援，出貨 = prod plane，必填）。
// VITE_IDENTITY_BASE（選填）= 釘住的 identity：infra/deploy 的 plane 部署填佔位、web/portal 回檔案時換成各 env 自己的
// （同一份 build 給 dev / prod，主機決定環境）；開發時也當啟動參數覆寫（e2e 打 test identity）。有設 → 只用它、不能切。
// 沒釘住 → 出貨版預設 prod、開發版建置（MODE = development）預設 dev；本機存的選擇（kinio.cloud_env）優先。

export type CloudEnv = 'prod' | 'dev';
/** 頂端色條 / 設備資訊的環境標籤；null = prod（完全不標示）。 */
export type CloudEnvLabel = 'dev' | 'test' | null;

export const CLOUD_ENVS: readonly CloudEnv[] = ['prod', 'dev'];
/** 本機存的選擇（localStorage），值 `prod` / `dev`；三平台同一個 key。 */
export const CLOUD_ENV_STORAGE_KEY = 'kinio.cloud_env';

/** 品牌 .env 的變數名（vite.config.ts 檢查、config.ts 讀，同一份）。 */
export const CLOUD_ENV_VARS = {
  prod: 'VITE_IDENTITY_BASE_PROD',
  dev: 'VITE_IDENTITY_BASE_DEV',
  api: 'VITE_API_BASE',
  pinned: 'VITE_IDENTITY_BASE',
} as const;

/**
 * Kinio plane 主機的網域後綴（品牌 .env 的 VITE_PLANE_HOST_SUFFIXES，逗號或空白分隔；SPEC #public-api C6）：配對深連結的 a / u
 * 只信任這些主機、dev server 的同源代理也只轉這些主機。程式裡不寫任何主機名，沒設就建置失敗（planeHostSuffixesConfigError）。
 * `.` 開頭 = 後綴（要多一段子網域），否則整個主機名相等。
 */
export const PLANE_HOST_SUFFIXES_VAR = 'VITE_PLANE_HOST_SUFFIXES';

export function parsePlaneHostSuffixes(v: string | undefined | null): string[] {
  return (v ?? '')
    .split(/[\s,]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
}

/** 主機（可帶 :port）是不是在後綴清單裡。 */
export function hostMatchesSuffixes(host: string, suffixes: readonly string[]): boolean {
  const h = host.toLowerCase().replace(/:\d+$/, '');
  return !!h && suffixes.some((s) => (s.startsWith('.') ? h.endsWith(s) && h.length > s.length : h === s));
}

/** 建置檢查：VITE_PLANE_HOST_SUFFIXES 沒設或格式不對 → 錯誤訊息。 */
export function planeHostSuffixesConfigError(env: Record<string, string | undefined>): string | null {
  const list = parsePlaneHostSuffixes(env[PLANE_HOST_SUFFIXES_VAR]);
  if (list.length > 0 && list.every((s) => /^\.?[a-z0-9.-]+$/.test(s))) return null;
  // l10n-ignore: 工程診斷（錯誤細節 / 建置設定錯誤；畫面文案依錯誤種類查字串表）
  return `品牌設定錯誤：${PLANE_HOST_SUFFIXES_VAR} 沒設或格式不對（品牌 web/.env，Kinio plane 主機的網域後綴，例 .example.com；沒設就建置失敗，SPEC #public-api C6）。`;
}

/** 開發版建置的頁籤標題前綴（index.html 與程式改 document.title 都套）。 */
export const DEV_BUILD_TITLE_PREFIX = '[開發版] ';  // l10n-ignore: 開發版建置的標示（只在開發版出現，同 App 名稱的「開發版」）

export function devBuildTitle(title: string, devBuild: boolean): string {
  return devBuild ? DEV_BUILD_TITLE_PREFIX + title : title;
}

/** 去尾斜線、前後空白（比較與組 URL 用）。 */
export function trimBase(s: string | undefined | null): string {
  return (s ?? '').trim().replace(/\/+$/, '');
}

export function parseCloudEnv(v: string | null | undefined): CloudEnv | null {
  return v === 'prod' || v === 'dev' ? v : null;
}

/** 出貨版 prod、開發版 dev；有存值就用存值（出貨版存了 dev = 測試人員切過去了）。 */
export function resolveCloudEnv(devBuild: boolean, stored: string | null | undefined): CloudEnv {
  return parseCloudEnv(stored) ?? (devBuild ? 'dev' : 'prod');
}

export interface IdentityBases {
  prod: string;
  dev: string;
}

/** 等於注入的 prod → null；等於 dev → dev；host 有 `.test.` 這一段 → test；其他 → dev。比較時去尾斜線、不分大小寫。 */
export function cloudEnvLabel(identityBase: string, bases: IdentityBases): CloudEnvLabel {
  const cur = trimBase(identityBase).toLowerCase();
  if (cur === trimBase(bases.prod).toLowerCase()) return null;
  if (cur === trimBase(bases.dev).toLowerCase()) return 'dev';
  let host = '';
  try {
    host = new URL(cur).hostname;
  } catch {
    /* 不是 URL：當 dev */
  }
  return host.includes('.test.') ? 'test' : 'dev';
}

function isHttpsUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return u.protocol === 'https:' && !!u.hostname;
  } catch {
    return false;
  }
}

/**
 * 建置檢查（vite.config.ts cloudEnvCheck，同 brandTabsCheck 的寫法）：PROD / DEV identity 與未登入 API 主機缺或不是 https:// → 錯誤訊息。
 * VITE_IDENTITY_BASE 有設時也要是 https://（開發版建置可以 http://，給本機 identity 的 e2e）。
 */
export function cloudEnvConfigError(env: Record<string, string | undefined>, devBuild: boolean): string | null {
  const bad: string[] = [];
  for (const k of [CLOUD_ENV_VARS.prod, CLOUD_ENV_VARS.dev, CLOUD_ENV_VARS.api]) {
    if (!isHttpsUrl(trimBase(env[k]))) bad.push(k);
  }
  const pinned = trimBase(env[CLOUD_ENV_VARS.pinned]);
  if (pinned && !isHttpsUrl(pinned) && !(devBuild && /^http:\/\/[^/]+/.test(pinned))) bad.push(CLOUD_ENV_VARS.pinned);
  if (bad.length === 0) return null;
  return (
    `品牌設定錯誤：${bad.join('、')} 沒設或不是 https:// 位址（品牌 web/.env，來源 brand.yaml identity.base_urls / prelogin_api_base；` +  // l10n-ignore: 工程診斷（錯誤細節 / 建置設定錯誤；畫面文案依錯誤種類查字串表）
    `沒設就建置失敗，不用預設位址，SPEC #platform-791）。`  // l10n-ignore: 工程診斷（錯誤細節 / 建置設定錯誤；畫面文案依錯誤種類查字串表）
  );
}

const DEV_ACCOUNT_SUFFIX = '+dev';

/**
 * `+dev` 帳號（SPEC #envs-dev-account，10-16 由 `.dev` 改成 `+dev`）：email 的 local part 以 `+dev` 結尾（不分大小寫）、前面還有名字 → 去掉 `+dev` 的
 * `<名>@<網域>`（前後空白先去掉，名字與網域照原樣）；其他 → null。三平台共用案例 docs/testdata/kinio-dev-account.json。
 */
export function devAccountEmail(email: string): string | null {
  const e = email.trim();
  const at = e.lastIndexOf('@');
  if (at <= 0 || at === e.length - 1) return null;
  const local = e.slice(0, at);
  if (local.length <= DEV_ACCOUNT_SUFFIX.length || local.slice(-DEV_ACCOUNT_SUFFIX.length).toLowerCase() !== DEV_ACCOUNT_SUFFIX) return null;
  return local.slice(0, -DEV_ACCOUNT_SUFFIX.length) + e.slice(at);
}

/** 登入頁送出前的判斷：[email] = 送給 identity 的 email；[switchTo] = 要先照切換流程切到哪（null = 不切）。 */
export interface DevAccountLogin {
  email: string;
  switchTo: CloudEnv | null;
}

/**
 * 只在出貨版、identity 沒釘住時判斷 `+dev`：是 dev 帳號 → 用去掉 `+dev` 的 email 登入，目前不在 dev 就先切到 dev；
 * 不是 → 照原樣（已在 dev 也不自動切回 prod，登入頁有「測試人員：切回 prod」）。開發版照舊手動選，email 照原樣送。
 */
export function devAccountLogin(email: string, devBuild: boolean, pinned: boolean, current: CloudEnv | null): DevAccountLogin {
  const dev = devBuild || pinned ? null : devAccountEmail(email);
  if (!dev) return { email, switchTo: null };
  return { email: dev, switchTo: current === 'dev' ? null : 'dev' };
}
